import React, { useMemo, useState } from 'react'
import { ButtonGroup, Drawer, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { toast } from '@reapit/elements/core/toaster'
import { useAdminFormsList } from '../../../../api/forms'
import { useCopyFormTemplates } from '../../../../api/form-templates'
import { getEnumShortName, getFormFamily, useAdminEnums } from '../../../../api/enums'
import { AdminFormListItem } from '../../../../api/types'
import { FieldHelp, FieldStack } from '../../../../components/form-details-fields/style'

/** "Tenancy Agreement · SA AU" — the plain name is ambiguous, since several forms share it across regions. */
const formOptionLabel = (form: AdminFormListItem, enums: ReturnType<typeof useAdminEnums>['data']): string => {
  const name = form.fullName || form.name || 'Untitled form'
  const region = getEnumShortName(enums, 'Region', form.region)
  const country = getEnumShortName(enums, 'Country', form.country)
  const location = [region, country].filter(Boolean).join(' ')
  return location ? `${name} · ${location}` : name
}

interface CopyTemplatesDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** The form receiving the copy (route param) — excluded from its own source-form picker. */
  formReferenceID: string
  /** The specific version receiving the copy — templates are version-scoped, so this is the real target. */
  versionReferenceID: string
  /** The target form's document family ("Agreement" | "Notice") — restricts the source-form picker to
   * same-family forms, so a copy can never bring in activities that can never fire on the target. */
  family?: string
  onCopied: () => void
}

/**
 * Drawer to clone another form's (current version's) event + action templates onto this version. Only
 * usable when this version has none of its own yet — the backend rejects the copy otherwise (409) so the
 * admin never loses manual work.
 */
export const CopyTemplatesDrawer: React.FC<CopyTemplatesDrawerProps> = ({
  isOpen,
  onOpenChange,
  formReferenceID,
  versionReferenceID,
  family,
  onCopied,
}) => {
  const formId = 'copy-templates-form'
  const { data: forms } = useAdminFormsList()
  const { data: enums } = useAdminEnums()
  const copyFormTemplates = useCopyFormTemplates()

  const [source, setSource] = useState<AdminFormListItem | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The list is now one row per form VERSION — a form with 3 versions appears 3 times. Copying pulls from a
  // source form's LATEST version, so dedupe down to one entry per form by selecting the max validFromUTC.
  // A form with no version yet has nothing to copy, so it's excluded too. When the target's family is
  // known, source forms from a different family are excluded too — their templates' activities can never
  // fire on the target family (e.g. an Agreement's SentForSigning event copied onto a Notice).
  const sourceOptions = useMemo(() => {
    const latestByForm = new Map<string, AdminFormListItem>()
    for (const form of forms ?? []) {
      if (form.referenceID === formReferenceID || !form.versionReferenceID) continue
      if (family && getFormFamily(enums, form.formType) !== family) continue
      const existing = latestByForm.get(form.referenceID)
      const existingDate = existing?.validFromUTC ?? ''
      const nextDate = form.validFromUTC ?? ''
      if (!existing || nextDate > existingDate) latestByForm.set(form.referenceID, form)
    }
    return Array.from(latestByForm.values())
  }, [forms, formReferenceID, family, enums])

  const handleClose = () => {
    setSource(null)
    setError(null)
    onOpenChange(false)
  }

  const handleSubmit = async () => {
    if (!source?.versionReferenceID) return
    setBusy(true)
    setError(null)
    try {
      const result = await copyFormTemplates(formReferenceID, versionReferenceID, source.versionReferenceID)
      toast.success(
        `Copied ${result.eventTemplatesCopied} notification(s) and ${result.actionTemplatesCopied} action(s).`,
      )
      onCopied()
      handleClose()
    } catch (thrown) {
      setError(thrown instanceof Error ? thrown.message : 'Could not copy templates from that form.')
    } finally {
      setBusy(false)
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={busy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={busy} disabled={busy || !source} type="submit" form={formId}>
        Copy
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer isOpen={isOpen} title="Copy Templates From Another Form" onClose={handleClose} footerItems={footer}>
      {isOpen && (
        <FieldStack
          id={formId}
          onSubmit={(event) => {
            event.preventDefault()
            handleSubmit()
          }}
        >
          <div>
            <Select
              label="Source Form *"
              options={sourceOptions}
              value={source}
              getOptionLabel={(option: AdminFormListItem) => formOptionLabel(option, enums)}
              getOptionValue={(option: AdminFormListItem) => option.referenceID}
              onChange={(option: AdminFormListItem | null) => setSource(option)}
              errorMessage={error ?? undefined}
            />
            <FieldHelp>
              Clones the source form&apos;s notifications and actions onto this form, then you edit from there. Only
              works while this form has none of its own — remove any you&apos;ve already added first if you want a clean
              copy.
            </FieldHelp>
          </div>
        </FieldStack>
      )}
    </Drawer>
  )
}
